import React, { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoon,
  faSun,
  faRightFromBracket,
  faHouse,
  faUsers,
  faClipboardList,
  faBars,
  faTimes,
  faFolderOpen,
  faComments,
  faUsersCog,
  faExclamationTriangle,
  faFileLines,
  faClipboardCheck,
  faFileAlt,
  faLanguage,
  faRobot,
  faListCheck,
  faRotateRight,
  faCommentDots,
  faTrophy,
  faAnglesLeft,
  faAnglesRight,
  faCalendarDays,
  faMoneyBillWave,
  faServer,
  faDiagramProject,
  faChartLine
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../contexts/AuthContext";
import {
  autoCheckIsOn,
  refreshAuthToken,
  requestLoginAfterExpiry,
  tokenIsExpired,
  tokenNeedsRenewal
} from "../../utils/authSession";
import { useApp } from "../../contexts/AppContext";
import SessionTimeoutModal from "../SessionTimeoutModal";
import useSessionTimeout from "../../hooks/useSessionTimeout";
import MessageBox from "../MessageBox";
import "../../App.css";

// Below this width the sidebar stops reserving space and behaves as an
// overlay drawer instead.
const MOBILE_BREAKPOINT = 900;

// Map URL paths to tab identifiers for active state
function getActiveTab(pathname) {
  if (pathname.startsWith('/dashboard')) return 'dashboard';
  if (pathname.startsWith('/chatbot')) return 'chatbot';
  if (pathname.startsWith('/rubrics')) return 'rubrics';
  if (pathname.startsWith('/scans')) return 'scans';
  if (pathname.startsWith('/evaluate/pipeline')) return 'pipeline';
  if (pathname.startsWith('/evaluate')) return 'evaluate';
  if (pathname.startsWith('/students')) return 'students';
  if (pathname.startsWith('/mcqs')) return 'mcqs';
  if (pathname.startsWith('/mistakes')) return 'mistakes';
  if (pathname.startsWith('/blank-pages')) return 'blank-pages';
  if (pathname.startsWith('/rechecks')) return 'rechecks';
  if (pathname.startsWith('/feedback')) return 'feedback';
  if (pathname.startsWith('/messages')) return 'messages';
  if (pathname.startsWith('/requests')) return 'requests';
  if (pathname.startsWith('/papers')) return 'subjective';
  if (pathname.startsWith('/users')) return 'users';
  if (pathname.startsWith('/access')) return 'access';
  if (pathname.startsWith('/tables')) return 'tables';
  if (pathname.startsWith('/plans-preparations')) return 'plans-preparations';
  if (pathname.startsWith('/curriculum')) return 'plans-preparations';
  if (pathname.startsWith('/ocr')) return 'ocr';
  if (pathname.startsWith('/board-results')) return 'board-results';
  if (pathname.startsWith('/fees')) return 'fees';
  if (pathname.startsWith('/work-log')) return 'work-log';
  return 'dashboard';
}

// Sidebar navigation, grouped into sections.
const NAV_GROUPS = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      { id: 'dashboard', path: '/dashboard', icon: faHouse, label: 'Dashboard', access: 'dashboard' },
      { id: 'chatbot', path: '/chatbot', icon: faRobot, label: 'ChatFast', access: 'chatbot' },
    ],
  },
  {
    id: 'assessment',
    label: 'Assessment',
    items: [
      { id: 'scans', path: '/scans', icon: faFolderOpen, label: 'Scans', access: 'scans' },
      { id: 'evaluate', path: '/evaluate', icon: faClipboardCheck, label: 'Evaluate', access: 'evaluate' },
      { id: 'rubrics', path: '/rubrics', icon: faClipboardList, label: 'Rubrics', access: 'rubrics' },
      { id: 'subjective', path: '/papers', icon: faFileAlt, label: 'Papers', access: 'subjective' },
      { id: 'mcqs', path: '/mcqs', icon: faListCheck, label: 'MCQs', access: 'mcqs' },
      { id: 'ocr', path: '/ocr', icon: faLanguage, label: 'OCR', access: 'ocr' },
    ],
  },
  {
    id: 'students',
    label: 'Students',
    items: [
      { id: 'students', path: '/students', icon: faUsers, label: 'Students', access: 'students' },
      { id: 'board-results', path: '/board-results', icon: faTrophy, label: 'Board Results', access: 'board-results' },
      { id: 'mistakes', path: '/mistakes', icon: faExclamationTriangle, label: 'Mistakes', access: 'mistakes' },
      { id: 'blank-pages', path: '/blank-pages', icon: faFileLines, label: 'Blank pages', access: 'mistakes' },
      { id: 'rechecks', path: '/rechecks', icon: faRotateRight, label: 'Re-checks', access: 'rechecks', badge: 'rechecks' },
      { id: 'feedback', path: '/feedback', icon: faCommentDots, label: 'Feedback', access: 'feedback' },
    ],
  },
  {
    id: 'school',
    label: 'School',
    items: [
      { id: 'plans-preparations', path: '/plans-preparations', icon: faCalendarDays, label: 'Plans & Prep', access: 'plans-preparations' },
      { id: 'messages', path: '/messages', icon: faComments, label: 'Messages', access: 'messages', badge: 'messages' },
      { id: 'requests', path: '/requests', icon: faServer, label: 'Requests', access: 'requests' },
      { id: 'pipeline', path: '/evaluate/pipeline', icon: faDiagramProject, label: 'Pipeline', access: 'pipeline' },
      { id: 'work-log', path: '/work-log', icon: faChartLine, label: 'Work log', access: 'work-log' },
    ],
  },
  {
    id: 'admin',
    label: 'Administration',
    items: [
      { id: 'users', path: '/users', icon: faUsersCog, label: 'Users', access: 'users' },
      { id: 'fees', path: '/fees', icon: faMoneyBillWave, label: 'Fees', access: 'fees' },
    ],
  },
];

const NAV_ITEMS = NAV_GROUPS.flatMap((group) => group.items);

export default function AppLayout() {
  const { currentUser, handleLogout, hasAccess } = useAuth();
  const { message, setMessage } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = getActiveTab(location.pathname);
  const activeItem = NAV_ITEMS.find((item) => item.id === activeTab);

  const [theme, setTheme] = useState(
    localStorage.getItem("theme") || "light"
  );
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    localStorage.getItem("sidebarCollapsed") === "true"
  );
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [pendingRechecks, setPendingRechecks] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const profileMenuRef = useRef(null);

  // Keep the Re-checks nav badge in sync with pending requests.
  useEffect(() => {
    if (!currentUser || !hasAccess('rechecks')) {
      setPendingRechecks(0);
      return;
    }
    let cancelled = false;
    const loadPending = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        const API_BASE_URL = window.API_BASE_URL;
        const res = await fetch(`${API_BASE_URL}/recheck-requests/pending-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data.success) {
          setPendingRechecks(Number(data.pending) || 0);
        }
      } catch (_) { /* non-critical */ }
    };
    loadPending();
    const id = setInterval(loadPending, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, currentUser?.role, location.pathname]);

  // Unread private Messages (e.g. recheck approve/reject notices).
  useEffect(() => {
    if (!currentUser || !hasAccess('messages')) {
      setUnreadMessages(0);
      return;
    }
    // Opening Messages clears unread; refresh badge after that.
    if (location.pathname.startsWith('/messages')) {
      setUnreadMessages(0);
    }
    let cancelled = false;
    const loadUnread = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        const API_BASE_URL = window.API_BASE_URL;
        const res = await fetch(`${API_BASE_URL}/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data.success) {
          // MessagesTab marks notices read on open; keep badge clear while viewing.
          if (location.pathname.startsWith('/messages')) {
            setUnreadMessages(0);
          } else {
            setUnreadMessages(Number(data.unread) || 0);
          }
        }
      } catch (_) { /* non-critical */ }
    };
    loadUnread();
    const id = setInterval(loadUnread, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, currentUser?.role, location.pathname]);

  // Session timeout. Auto-check never reaches this: the idle timer renews
  // itself while that switch is on.
  const handleSessionTimeout = () => {
    console.log('[Session] Session timeout - logging out user');
    handleLogout();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    const onExpired = () => {
      handleLogout();
      navigate('/login', { replace: true });
    };
    window.addEventListener('fg-session-expired', onExpired);
    return () => window.removeEventListener('fg-session-expired', onExpired);
  }, [handleLogout, navigate]);

  // While auto-check is on, renew the login before the 24h token dies.
  useEffect(() => {
    if (!currentUser) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled || !autoCheckIsOn()) return;
      const token = localStorage.getItem('authToken');
      if (!token || !tokenNeedsRenewal(token)) return;
      const renewed = await refreshAuthToken();
      if (cancelled || renewed) return;
      if (tokenIsExpired(token)) requestLoginAfterExpiry();
    };
    tick();
    const id = setInterval(tick, 15 * 60 * 1000);
    const onVisible = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [currentUser]);

  const sessionTimeout = useSessionTimeout(handleSessionTimeout);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // The drawer is a mobile-only affordance; navigating away should close it.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Close the profile menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    function handleEscape(event) {
      if (event.key === "Escape") setMobileNavOpen(false);
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const onLogout = () => {
    handleLogout();
    navigate('/', { replace: true });
  };

  const toggleNav = () => {
    if (window.innerWidth <= MOBILE_BREAKPOINT) {
      setMobileNavOpen((open) => !open);
    } else {
      setSidebarCollapsed((collapsed) => !collapsed);
    }
  };

  const badgeCountFor = (item) => {
    if (item.badge === 'rechecks') return pendingRechecks;
    if (item.badge === 'messages') return unreadMessages;
    return 0;
  };

  const rootClass = [
    "app-root",
    sidebarCollapsed ? "sidebar-collapsed" : "",
    mobileNavOpen ? "nav-open" : ""
  ].filter(Boolean).join(" ");

  return (
    <div className={rootClass}>
      <SessionTimeoutModal
        showWarning={sessionTimeout.showWarning}
        warningCountdown={sessionTimeout.warningCountdown}
        onContinue={sessionTimeout.extendSession}
      />

      <header className="app-header">
        <div className="header-left">
          <button
            className="nav-toggle"
            onClick={toggleNav}
            aria-label={mobileNavOpen ? "Close navigation" : "Toggle navigation"}
            title="Toggle navigation"
          >
            <FontAwesomeIcon icon={mobileNavOpen ? faTimes : faBars} />
          </button>

          <div className="logo-container" onClick={() => navigate('/dashboard')}>
            <img
              src={`${process.env.PUBLIC_URL}/Logo-FastGrader.png`}
              alt="FastGrader Logo"
              className="logo-image"
            />
            <h2>FastGrader</h2>
          </div>

          {activeItem && (
            <span className="header-page-title">{activeItem.label}</span>
          )}
        </div>

        <div className="header-actions">
          {/* Theme Toggle */}
          <div
            className={`theme-toggle-switch ${theme === 'dark' ? 'dark' : ''}`}
            onClick={toggleTheme}
            title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
          >
            <div className="toggle-track">
              <span className="toggle-icon sun">
                <FontAwesomeIcon icon={faSun} />
              </span>
              <span className="toggle-icon moon">
                <FontAwesomeIcon icon={faMoon} />
              </span>
              <div className="toggle-thumb"></div>
            </div>
          </div>

          {/* User Profile with Dropdown */}
          <div style={{ position: "relative" }} ref={profileMenuRef}>
            <div
              className="user-profile-card"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              style={{ cursor: "pointer", userSelect: "none" }}
            >
              <div className="user-avatar">
                {(currentUser?.name || currentUser?.username || "U").charAt(0).toUpperCase()}
              </div>
              <div className="user-details">
                <span className="user-name">
                  {currentUser?.name || currentUser?.username}
                </span>
                <span className="user-role">
                  {currentUser?.role}
                </span>
              </div>
              <div style={{
                marginLeft: "var(--spacing-sm)",
                fontSize: "0.75rem",
                color: "var(--text-muted)",
                transition: "transform var(--transition-base)",
                transform: showProfileMenu ? "rotate(180deg)" : "rotate(0deg)"
              }}>
                ▼
              </div>
            </div>

            {showProfileMenu && (
              <div className="profile-dropdown">
                <button
                  onClick={() => {
                    onLogout();
                    setShowProfileMenu(false);
                  }}
                  className="dropdown-item"
                >
                  <FontAwesomeIcon icon={faRightFromBracket} />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <aside className="app-sidebar" aria-label="Main navigation">
        <nav className="sidebar-nav">
          {NAV_GROUPS.map((group) => {
            const items = group.items.filter((item) => hasAccess(item.access));
            if (items.length === 0) return null;
            return (
              <div className="sidebar-group" key={group.id}>
                <div className="sidebar-group-label">{group.label}</div>
                {items.map((item) => {
                  const count = badgeCountFor(item);
                  const description = count > 0 ? `${item.label} (${count})` : item.label;
                  return (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      onClick={() => setMobileNavOpen(false)}
                      className={`sidebar-item ${activeTab === item.id ? "active" : ""}`}
                      // Real links enable right-click → Open in new tab, plus
                      // middle-click / Ctrl/Cmd-click. Collapsed rail hides
                      // labels, so the native tooltip is the only identifier.
                      title={sidebarCollapsed || count > 0 ? description : undefined}
                      aria-label={description}
                    >
                      <span className="sidebar-icon">
                        <FontAwesomeIcon icon={item.icon} />
                        {count > 0 && <span className="sidebar-dot" aria-hidden="true" />}
                      </span>
                      <span className="sidebar-label">{item.label}</span>
                      {count > 0 && (
                        <span className="sidebar-badge">
                          {count > 99 ? '99+' : count}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
            title={sidebarCollapsed ? "Expand menu" : "Collapse menu"}
          >
            <FontAwesomeIcon icon={sidebarCollapsed ? faAnglesRight : faAnglesLeft} />
            <span className="sidebar-label">Collapse</span>
          </button>
        </div>
      </aside>

      <div
        className="sidebar-overlay"
        onClick={() => setMobileNavOpen(false)}
        aria-hidden="true"
      />

      <main>
        <Outlet />
      </main>

      <MessageBox
        message={message}
        onClose={() => setMessage({ show: false, text: '', type: 'info' })}
      />
    </div>
  );
}
